import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { useRequireAuth } from "@/components/auth/useRequireAuth";
import ThemedPageLayout from "@/templates/ThemedPageLayout";
// Relative on purpose: pure modules and components, resolvable under Jest without a shim.
import LoadFailure from "../components/ui/LoadFailure";
import usePublicPathway from "../pathways/usePublicPathway";
import { fetchCheckoutStatus, fetchMemberPathway } from "../pathways/learnerApi";
import { checkoutReturn } from "../pathways/learnerModel";
import { PATHWAYS_LEARNER as IDS } from "../constants/testIds/pathwaysLearner";
import PathwayDetail from "./pathways/PathwayDetail";
import PathwayHome from "./pathways/PathwayHome";
import PathwayNotFound from "./pathways/PathwayNotFound";

/** How many times the page re-reads the membership after a paid return, and how far apart. */
export const CONFIRM_TRIES = 8;
export const CONFIRM_INTERVAL_MS = 1500;

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

const Spinner = () => (
  <div className="flex justify-center py-16">
    <Loader2 className="animate-spin text-eh-sage-deep" size={28} aria-hidden="true" />
  </div>
);

/**
 * `/parcours/:pathwayId`: the card detail and the join form for a visitor, the pathway
 * home for its learner (contract §9).
 *
 * WHICH ONE. The public detail is read first (anyone may read it):
 *
 *   public detail            viewer        shows
 *   ───────────────────────  ────────────  ───────────────────────────────────────────
 *   200, viewer_enrolled     signed in     the HOME
 *   200, not enrolled        anyone        the DETAIL; a successful join switches to the
 *                                          home without a page load
 *   404                      signed in     the HOME, whose own 404 is "not found": an
 *                                          ARCHIVED pathway is hidden from the public but
 *                                          still readable by its learners
 *   404                      signed out    "not found", with a sign-in button
 *   other failure            anyone        load failure with retry
 *
 * THE CHECKOUT RETURN (contract §5.3). Stripe sends the buyer back to this page with
 * `?success=1&session_id=…`. `GET /checkout/connect/status/{session_id}` is asked first;
 * "paid" TRIGGERS fulfilment server-side, it does not wait for it (the course player's
 * lesson, public/LearnPlayer.jsx), so the membership is then re-read
 * (`GET /client/pathways/{id}`) until it answers 200, bounded. On success the query is
 * dropped from the URL and the home is shown. A session that is not paid says so; a poll
 * that runs out says the payment went through and access is on its way, offers "Check
 * again" (and a refresh re-runs the check too, since the query is kept), and does NOT
 * offer the join form, which would open a second checkout. `?cancel=1` is a notice that
 * nothing was charged.
 *
 * LOGIN GATE through `useRequireAuth`, the gate every public page uses: it owns the
 * sign-in dialog and the return leg of the Google auth bridge on tenant hosts.
 */
export default function PathwayMember() {
  const t = useT("public");
  const { pathwayId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useRequireAuth();
  const { user, loading: authLoading, ensureAuthed, authDialog } = auth;
  const pub = usePublicPathway(pathwayId, user?.email || "");
  // Set by a successful join or a confirmed payment on THIS page: the home is shown without
  // waiting for the public detail to be re-read.
  const [joined, setJoined] = useState(false);
  // "idle" | "confirming" | "slow" | "unpaid" | "cancelled"
  const [checkout, setCheckout] = useState("idle");
  // Bumped by "Check again" after a slow confirmation: re-runs the check below.
  const [attempt, setAttempt] = useState(0);
  const back = useMemo(() => checkoutReturn(location.search), [location.search]);

  useEffect(() => {
    if (back.kind === "cancelled") {
      setCheckout("cancelled");
      navigate({ pathname: location.pathname, search: "" }, { replace: true });
      return undefined;
    }
    if (back.kind !== "paid_return" || authLoading) return undefined;
    let alive = true;
    setCheckout("confirming");
    (async () => {
      let status;
      try {
        status = (await fetchCheckoutStatus(back.sessionId)).status;
      } catch {
        // The money may have been taken: say "on its way", never "not paid".
        if (alive) setCheckout("slow");
        return;
      }
      if (!alive) return;
      if (status !== "paid") { setCheckout("unpaid"); return; }
      for (let i = 0; i < CONFIRM_TRIES; i += 1) {
        try {
          await fetchMemberPathway(pathwayId);
          if (!alive) return;
          setJoined(true);
          setCheckout("idle");
          toast.success(t("pathways.checkout.paidToast"));
          navigate({ pathname: location.pathname, search: "" }, { replace: true });
          return;
        } catch {
          // 404 until fulfilment lands; retry.
        }
        if (i < CONFIRM_TRIES - 1) await sleep(CONFIRM_INTERVAL_MS);
        if (!alive) return;
      }
      setCheckout("slow");
    })();
    return () => { alive = false; };
    // `t`, `navigate` and `location.pathname` are read at the moment of use; the check
    // itself is keyed by the return it answers and by the session being resolved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [back.kind, back.sessionId, authLoading, pathwayId, attempt]);

  let body;
  if (checkout === "slow") {
    // Paid, access not visible yet: the join form is NOT offered again, since a second
    // press would open a second checkout for a pathway already paid for.
    body = (
      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => setAttempt((n) => n + 1)}
          data-testid={IDS.checkoutRecheck}
          className="inline-flex h-11 items-center rounded-full bg-eh-ink px-6 text-sm font-medium text-eh-cream"
        >
          {t("pathways.checkout.recheck")}
        </button>
      </div>
    );
  } else if (checkout === "confirming") {
    body = (
      <div data-testid={IDS.checkoutConfirming} role="status" className="flex flex-col items-center gap-3 py-16 text-eh-muted">
        <Loader2 className="animate-spin text-eh-sage-deep" size={28} aria-hidden="true" />
        <p>{t("pathways.checkout.confirming")}</p>
      </div>
    );
  } else if (authLoading || (pub.loading && !pub.data && !pub.notFound)) {
    body = <Spinner />;
  } else if (user && (joined || pub.notFound || pub.data?.viewer_enrolled)) {
    body = <PathwayHome key={pathwayId} pathwayId={pathwayId} />;
  } else if (pub.notFound) {
    body = <PathwayNotFound onSignIn={() => ensureAuthed()} />;
  } else if (pub.error || !pub.data) {
    body = (
      <LoadFailure
        message={t("pathways.loadFailed")}
        onRetry={pub.reload}
        retryLabel={t("pathways.retry")}
        testId={IDS.loadError}
      />
    );
  } else {
    body = (
      <PathwayDetail
        detail={pub.data}
        auth={auth}
        onEnrolled={() => setJoined(true)}
        onFull={pub.reload}
      />
    );
  }

  return (
    <ThemedPageLayout>
      <section className="pt-32 pb-24 px-4 sm:px-6">
        {checkout === "slow" && <Notice testId={IDS.checkoutSlow}>{t("pathways.checkout.slow")}</Notice>}
        {checkout === "unpaid" && <Notice testId={IDS.checkoutUnpaid}>{t("pathways.checkout.unpaid")}</Notice>}
        {checkout === "cancelled" && <Notice testId={IDS.checkoutCancelled}>{t("pathways.checkout.cancelled")}</Notice>}
        {body}
      </section>
      {authDialog}
    </ThemedPageLayout>
  );
}

function Notice({ testId, children }) {
  return (
    <p role="status" data-testid={testId} className="mx-auto mb-5 max-w-xl rounded-2xl border border-eh-border-soft bg-eh-cream-deep px-4 py-3 text-sm text-eh-ink">
      {children}
    </p>
  );
}
